import { DEMO_ACTIONSTEP_MATTERS } from "@/lib/outlookActionstepDemo";
import { isLacwLocalTestMode } from "@/lib/lacwLocalTestStore";
import { WALKTHROUGH_ACTIONSTEP_MATTER } from "@/lib/remindersDemo";

const STORAGE_KEY = "cls_lacw_actionstep_simulator_v1";
export const ACTIONSTEP_SIMULATOR_EVENT = "cls-actionstep-simulator-changed";

const ENTITY_LABELS = {
  fileNotes: "File note",
  tasks: "Task",
  documents: "Document",
  appointments: "Appointment",
  timeEntries: "Time entry",
};

// Raise the version when the seeded matters change, so browsers holding older simulator data reseed.
const SIMULATOR_VERSION = 5;
// The three sample matters, then Zara Walkthrough (10455), the client of the walkthrough and the sample letters.
export const SIMULATOR_SEED_MATTERS = [...DEMO_ACTIONSTEP_MATTERS, WALKTHROUGH_ACTIONSTEP_MATTER];
const FOLDER_DEFINITIONS = [
  { name: "VLA Funding docs" },
  { name: "ATLAS Letters", parentName: "VLA Funding docs" },
  { name: "Support materials" },
  { name: "File notes" },
  { name: "Court documents" },
  { name: "Correspondence - other" },
  { name: "Correspondence - client" },
  { name: "BOE and disclosure" },
  { name: "Document Exchange" },
];

function seededRecords(matters) {
  const folders = [];
  const documents = [];
  const fileNotes = [];
  const tasks = [];
  const appointments = [];
  const timeEntries = [];
  const activity = [];

  matters.forEach((matter, matterIndex) => {
    const matterId = String(matter.externalId);
    const matterFolders = Object.fromEntries(FOLDER_DEFINITIONS.map(({ name }, folderIndex) => [name, `AS-FOLDER-${matterId}-${folderIndex + 1}`]));
    FOLDER_DEFINITIONS.forEach(({ name, parentName }, folderIndex) => {
      const folder = { id: matterFolders[name], matterId, name, parentId: parentName ? matterFolders[parentName] : "", parentName: parentName || "", simulated: true };
      folders.push(folder);
    });
    const samples = [
      ["VLA Funding docs", matter.matter.fundingType === "Legal Aid" ? "VLA grant of aid.pdf" : "Funding position note.pdf"],
      ["Support materials", "Client details and instructions.pdf"],
      ["File notes", "Initial conference file note.pdf"],
      ["Court documents", matterIndex === 1 ? "Bail application - draft.pdf" : "Court attendance notice.pdf"],
      ["Correspondence - client", "Email to client - upcoming court date.pdf"],
      ["BOE and disclosure", "Police brief index.pdf"],
      ["Document Exchange", "Document exchange index.pdf"],
    ];
    samples.forEach(([folderName, name], documentIndex) => documents.push({
      id: `AS-DOC-${matterId}-${documentIndex + 1}`,
      matterId,
      folderId: matterFolders[folderName],
      folderName,
      name,
      size: 48000 + documentIndex * 12750,
      contentType: "application/pdf",
      createdAt: `2026-08-${String(1 + matterIndex).padStart(2, "0")}T0${documentIndex}:30:00.000Z`,
      simulated: true,
    }));
    fileNotes.push({ id: `AS-NOTE-${matterId}-1`, matterId, text: "Initial instructions received and fictional matter opened for portal testing.", source: "Actionstep Simulator", createdAt: "2026-08-01T01:00:00.000Z", simulated: true });
    fileNotes.push({ id: `AS-NOTE-${matterId}-2`, matterId, text: `Reviewed upcoming ${matter.matter.appearanceType.toLowerCase()} and confirmed sample preparation steps.`, source: "Actionstep Simulator", createdAt: "2026-08-04T03:15:00.000Z", simulated: true });
    tasks.push({ id: `AS-TASK-${matterId}-1`, matterId, title: "Confirm client instructions before court", status: "Incomplete", dueDate: matter.matter.nextAppearanceDate, createdAt: "2026-08-04T04:00:00.000Z", simulated: true });
    tasks.push({ id: `AS-TASK-${matterId}-2`, matterId, title: "Review disclosure and update counsel brief", status: matterIndex === 0 ? "Complete" : "Incomplete", dueDate: "2026-08-10", createdAt: "2026-08-04T04:05:00.000Z", simulated: true });
    appointments.push({ id: `AS-APPT-${matterId}-1`, matterId, subject: `${matter.matter.appearanceType} - ${matter.client.fullName}`, startsAt: `${matter.matter.nextAppearanceDate}T09:30:00+10:00`, location: matter.matter.court, createdAt: "2026-08-04T05:00:00.000Z", simulated: true });
    timeEntries.push({ id: `AS-TIME-${matterId}-1`, matterId, description: "Review file and prepare court attendance", hours: 1.2 + matterIndex * 0.2, rate: 180, billable: true, createdAt: "2026-08-05T02:00:00.000Z", simulated: true });
    activity.push({ id: `AS-ACTIVITY-${matterId}-1`, matterId, createdAt: "2026-08-05T02:00:00.000Z", description: "Sample matter records populated", recordId: matterId });
  });
  return { folders, documents, fileNotes, tasks, appointments, timeEntries, activity };
}

function storage() {
  if (!isLacwLocalTestMode()) throw new Error("The Actionstep simulator is available only in LACW local test mode");
  return window.localStorage;
}

function seed() {
  const matters = SIMULATOR_SEED_MATTERS.map((item, index) => ({
    ...item,
    externalId: String(10452 + index),
    actionstepStatus: "Active",
    assignedTo: index === 0 ? "Alex Morgan (Sample)" : "Jordan Lee (Sample)",
    customData: {
      lacwMatterReference: item.client.fileNumber,
      fundingType: item.matter.fundingType,
      grantOfAidNumber: item.matter.grantOfAidNumber,
    },
  }));
  return {
    version: SIMULATOR_VERSION,
    failureMode: "none",
    matters,
    ...seededRecords(matters),
  };
}

function read() {
  const raw = storage().getItem(STORAGE_KEY);
  if (!raw) {
    const initial = seed();
    write(initial);
    return initial;
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed.version !== SIMULATOR_VERSION) {
      const upgraded = seed();
      write(upgraded);
      return upgraded;
    }
    return { ...seed(), ...parsed };
  } catch { return seed(); }
}

function write(data) {
  storage().setItem(STORAGE_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent(ACTIONSTEP_SIMULATOR_EVENT));
}

function id(prefix) {
  return `AS-${prefix.toUpperCase()}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function assertAvailable(data) {
  if (data.failureMode === "expired_token") throw new Error("Simulated Actionstep authentication expired. Reconnect and retry.");
  if (data.failureMode === "rate_limit") throw new Error("Simulated Actionstep rate limit (429). Retry after 30 seconds.");
  if (data.failureMode === "validation") throw new Error("Simulated Actionstep validation failure: a required matter field is missing.");
}

function add(type, matterId, payload) {
  const data = read();
  assertAvailable(data);
  const matter = data.matters.find((item) => String(item.externalId) === String(matterId));
  if (!matter) throw new Error("Simulated Actionstep matter was not found");
  const created = {
    id: id(type.slice(0, 4)),
    matterId: String(matterId),
    createdAt: new Date().toISOString(),
    simulated: true,
    ...payload,
  };
  data[type].unshift(created);
  data.activity.unshift({ id: id("activity"), matterId: String(matterId), createdAt: created.createdAt, description: `${ENTITY_LABELS[type]} created`, recordId: created.id });
  write(data);
  return created;
}

export const actionstepSimulatorStore = {
  snapshot() { return read(); },
  searchMatters(query) {
    const data = read();
    assertAvailable(data);
    const term = String(query || "").trim().toLowerCase();
    return data.matters.filter((item) => !term || [item.externalId, item.client.fullName, item.client.fileNumber, item.matter.summary, item.matter.court].some((value) => String(value || "").toLowerCase().includes(term)));
  },
  createFileNote(matterId, text, source = "CLS Portal — LACW") { return add("fileNotes", matterId, { text: String(text || "").trim(), source }); },
  createDocument(matterId, file) {
    const data = read();
    const folder = data.folders.find((item) => item.matterId === String(matterId) && (item.id === file?.folderId || item.name === file?.folderName))
      || data.folders.find((item) => item.matterId === String(matterId) && item.name === "Correspondence - other");
    return add("documents", matterId, { name: file?.name || "Sample document.pdf", size: Number(file?.size || 0), contentType: file?.type || "application/octet-stream", folderId: folder?.id || "", folderName: folder?.name || "Unfiled", atlasMessageId: file?.atlasMessageId || "", atlasFileExt: file?.atlasFileExt || "", atlasDocumentType: file?.atlasDocumentType || "", source: file?.source || "Portal" });
  },
  createTask(matterId, payload) { return add("tasks", matterId, { title: payload.title || "Sample follow-up", status: payload.status || "Incomplete", dueDate: payload.dueDate || "" }); },
  createAppointment(matterId, payload) { return add("appointments", matterId, { subject: payload.subject || "Sample court appearance", startsAt: payload.startsAt || new Date().toISOString(), location: payload.location || "Melbourne Magistrates' Court" }); },
  createTimeEntry(matterId, payload) { return add("timeEntries", matterId, { description: payload.description || "Sample legal work", hours: Number(payload.hours || 1), rate: Number(payload.rate || 180), billable: payload.billable !== false }); },
  setFailureMode(failureMode) { const data = read(); data.failureMode = failureMode; write(data); },
  reset() { const data = seed(); write(data); return data; },
};
